import { createFileRoute } from "@tanstack/react-router";
import { MerchantExperienceBar } from "@/components/renata/MerchantExperienceBar";
import { useCallback, useEffect, useState } from "react";
import { ClinicalPageShell } from "@/components/clinical";
import { CrudShellHeader, Loading } from "@/components/ds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, PackageOpen, Pencil, Plus, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { apiDelete, apiGetJson, apiSendJson } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import type { SessionPack } from "@/lib/db-service/service-packs";
import type { ServiceCatalogItem } from "@/lib/db-service/service-catalog";

export const Route = createFileRoute("/app/packs")({
  component: Page,
  head: () => ({ meta: [{ title: "Packs" }] }),
});

function Page() {
  const tenantId = useWorkspaceTenantId();
  const [packs, setPacks] = useState<SessionPack[]>([]);
  const [services, setServices] = useState<ServiceCatalogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<SessionPack | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SessionPack | null>(null);
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState("");
  const [sessionCount, setSessionCount] = useState("10");
  const [price, setPrice] = useState("");
  const [expiryDays, setExpiryDays] = useState("");
  const [servicesPick, setServicesPick] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const [p, s] = await Promise.all([
        apiGetJson<{ items: SessionPack[] }>("/api/service-admin?resource=packs", tenantId),
        apiGetJson<{ items: ServiceCatalogItem[] }>("/api/service-admin?resource=services", tenantId),
      ]);
      setPacks(p.items ?? []);
      setServices((s.items ?? []).filter((svc) => svc.active));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudo cargar");
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  function openNew() {
    setEditing(null);
    setName("");
    setSessionCount("10");
    setPrice("");
    setExpiryDays("");
    const m: Record<string, boolean> = {};
    for (const s of services) m[s.id] = false;
    setServicesPick(m);
    setOpen(true);
  }

  function openEdit(p: SessionPack) {
    setEditing(p);
    setName(p.name);
    setSessionCount(String(p.sessionCount));
    setPrice(String(p.price));
    setExpiryDays(p.expiryDays != null ? String(p.expiryDays) : "");
    const m: Record<string, boolean> = {};
    for (const s of services) m[s.id] = p.serviceIds.includes(s.id);
    setServicesPick(m);
    setOpen(true);
  }

  async function save() {
    if (!tenantId || !name.trim()) return;
    setSaving(true);
    try {
      const selectedServices = Object.entries(servicesPick).filter(([, v]) => v).map(([k]) => k);
      const body = {
        name: name.trim(),
        sessionCount: Number(sessionCount) || 10,
        price: Number(price) || 0,
        serviceIds: selectedServices,
        expiryDays: expiryDays.trim() ? Number(expiryDays) : null,
      };
      if (editing) {
        await apiSendJson(`/api/service-admin?resource=packs&id=${editing.id}`, "PATCH", body, tenantId);
      } else {
        await apiSendJson("/api/service-admin?resource=packs", "POST", body, tenantId);
      }
      toast.success(editing ? "Pack actualizado" : "Pack creado");
      setOpen(false);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(p: SessionPack) {
    if (!tenantId) return;
    try {
      await apiSendJson(`/api/service-admin?resource=packs&id=${p.id}`, "PATCH", { isActive: !p.isActive }, tenantId);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error");
    }
  }

  async function confirmDelete() {
    if (!deleteTarget || !tenantId) return;
    try {
      await apiDelete(`/api/service-admin?resource=packs&id=${deleteTarget.id}`, tenantId);
      toast.success("Pack eliminado");
      setDeleteTarget(null);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudo eliminar");
    }
  }

  return (
    <ClinicalPageShell offset="none" className="pt-6">
      <MerchantExperienceBar screenId="servicios-packs" />
      <CrudShellHeader
        title="Packs"
        count={packs.length}
        actions={
          <Button size="sm" className="gap-2" onClick={openNew}>
            <Plus className="h-4 w-4" /> Nuevo pack
          </Button>
        }
      />
      {loading ? (
        <Loading />
      ) : (
        <div className="space-y-4 px-4 pb-8">
          <p className="text-sm text-muted-foreground">Creá packs prepago de N sesiones para ofrecer a tus clientes.</p>
          {packs.length === 0 ? (
            <div className="rounded-xl border border-dashed py-16 text-center text-muted-foreground">
              <PackageOpen className="mx-auto mb-3 h-10 w-10 opacity-30" />
              <p className="text-sm">Todavía no creaste ningún pack.</p>
              <p className="mt-1 text-xs">Los packs permiten vender sesiones en bloque a precio preferencial.</p>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {packs.map((pack) => (
                <div
                  key={pack.id}
                  className={cn("group rounded-xl border bg-card p-5 transition-all hover:border-primary/30", !pack.isActive && "opacity-60")}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold">{pack.name}</h3>
                        {!pack.isActive && (
                          <Badge variant="outline" className="text-[10px] text-muted-foreground/60">
                            Inactivo
                          </Badge>
                        )}
                      </div>
                      <div className="mt-2 space-y-1">
                        <p className="text-xs text-muted-foreground">
                          <span className="font-semibold text-foreground">{pack.sessionCount}</span> sesiones ·{" "}
                          <span className="font-semibold text-foreground">${pack.price.toLocaleString("es-AR")}</span>
                        </p>
                        {pack.expiryDays != null && (
                          <p className="text-xs text-muted-foreground">Vence en {pack.expiryDays} días desde la compra</p>
                        )}
                        {pack.serviceIds.length > 0 ? (
                          <p className="text-xs text-muted-foreground/70">
                            Válido para {pack.serviceIds.length} servicio{pack.serviceIds.length > 1 ? "s" : ""}
                          </p>
                        ) : (
                          <p className="text-xs text-muted-foreground/70">Válido para todos los servicios</p>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(pack)}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 opacity-0 transition-opacity group-hover:opacity-100"
                        onClick={() => setDeleteTarget(pack)}
                      >
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    <p className="text-[10px] text-muted-foreground/60">
                      ${(pack.price / pack.sessionCount).toFixed(0)} / sesión
                    </p>
                    <button className="text-[10px] text-muted-foreground/60 hover:text-muted-foreground" onClick={() => void toggleActive(pack)}>
                      {pack.isActive ? "Desactivar" : "Activar"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar pack" : "Nuevo pack"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs">Nombre del pack</Label>
              <Input className="mt-1" value={name} onChange={(e) => setName(e.target.value)} placeholder="Pack 10 clases pilates" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Cantidad de sesiones</Label>
                <Input type="number" min={1} className="mt-1" value={sessionCount} onChange={(e) => setSessionCount(e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">Precio total ($)</Label>
                <Input type="number" min={0} className="mt-1" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="8000" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Vencimiento (días desde compra — opcional)</Label>
              <Input type="number" min={1} className="mt-1" value={expiryDays} onChange={(e) => setExpiryDays(e.target.value)} placeholder="Sin vencimiento" />
              <p className="mt-1 text-[10px] text-muted-foreground">Dejá vacío para que no venza.</p>
            </div>
            {services.length > 0 && (
              <div>
                <Label className="mb-2 block text-xs">Válido para servicios (opcional)</Label>
                <p className="mb-2 text-[10px] text-muted-foreground">Sin selección = aplica a todos los servicios.</p>
                <div className="max-h-32 space-y-2 overflow-y-auto rounded-lg border p-3">
                  {services.map((s) => (
                    <label key={s.id} className="flex items-center gap-2 text-sm">
                      <Checkbox checked={!!servicesPick[s.id]} onCheckedChange={(v) => setServicesPick((prev) => ({ ...prev, [s.id]: !!v }))} />
                      {s.name}
                    </label>
                  ))}
                </div>
              </div>
            )}
            {sessionCount && price && (
              <p className="text-xs text-muted-foreground/70">
                Precio por sesión: ${(Number(price) / Number(sessionCount)).toFixed(0)}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancelar
            </Button>
            <Button disabled={saving || !name.trim()} onClick={() => void save()}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteTarget != null} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar pack?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará <strong>{deleteTarget?.name}</strong> de forma permanente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => void confirmDelete()}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ClinicalPageShell>
  );
}
